import React, { useEffect, useState } from 'react';
import { Form, Schema, Stack, Col, Grid, Row, IconButton, Divider, FormInstance} from 'rsuite';
import PageContent from '@/components/PageContent';
import { BaseErrorModel, BaseResponseModel, LatLng } from '@/models/core.model';
import { useNavigate } from 'react-router-dom';
import PageNextIcon from '@rsuite/icons/PageNext';
import PagePreviousIcon from '@rsuite/icons/PagePrevious';
import ErrorMessage from '@/components/ErrorMessage';
import { AxiosError } from 'axios';
import { toastSuccessMessage } from '@/utils/toasterMessage';
import Textarea from '@/components/Textarea';
import { GoogleMap, Polygon, useJsApiLoader } from '@react-google-maps/api';
import { getBoundingBoxCenter, getPolygonPathsFromGeoJson } from '@/utils/map';
import { hk_lat_lng } from '@/constants/map';
import { City, CityGeometry, UpsertCityGeometry } from '@/models/city.model';
import { upsertCityGeometry } from '@/services/city.service';

const CityGeometrySchema = Schema.Model<UpsertCityGeometry>({
  cityId: Schema.Types.NumberType().isRequired('This field is required.'),
  jsonGeometry: Schema.Types.StringType().isRequired('This field is required.'),
});

interface CityGeometryFormProps{
  city?: City,
  jsonGeometry?: string;
  cityFetchData?: ()=>void;
};
const emptyModel : UpsertCityGeometry = {
  cityId: undefined,
  jsonGeometry: undefined,
};

const CityGeometryForm: React.FC<CityGeometryFormProps> = ({city, jsonGeometry, cityFetchData}) => {
  const [formValue, setFormValue] = useState<UpsertCityGeometry>(emptyModel);
  const [formError, setFormError] = useState({});
  const [errorModel, setErrorModel] = useState<BaseErrorModel|undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [polygonPaths, setPolygonPaths] = useState<LatLng[]>([]);
  const [mapCenter, setMapCenter] = useState<LatLng>();
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_MAP_API_KEY!,
  });
  const nav = useNavigate();
  const formRef = React.useRef<FormInstance<Record<string, any>>>(null);

  useEffect(() => {
    setFormValue({cityId: city?.id, jsonGeometry});
    setMapCenterByCity(city);
    updatePolygon(jsonGeometry ?? '');
  }, [city, jsonGeometry]);

  const setMapCenterByCity = (city: City|undefined) => {
    if(city && city.lat && city.lon) {
      setMapCenter({lat: city.lat, lng: city.lon}); 
    } else {
      setMapCenter(hk_lat_lng);
    } 
  };

  const handleSubmit = async () => {
    // console.log(formValue);
    if (formRef.current && !formRef.current.check()) {
      console.log(formError);
      return;
    }
    setErrorModel(undefined);
    setLoading(true);
    try {
      const result: BaseResponseModel<CityGeometry> = await upsertCityGeometry(formValue);
      if (result.success) {
        cityFetchData && cityFetchData();
        toastSuccessMessage('City Geometry saved successfully.');
      }
    } catch (err) {
      setErrorModel((err as AxiosError<BaseErrorModel>).response?.data);
    }finally{
      setLoading(false);
    }
  };

  const updatePolygon = (geoJson: string) => {
    const polygons = getPolygonPathsFromGeoJson(geoJson);
    if(polygons && polygons.length > 0) {
      setPolygonPaths(polygons);
      setMapCenter(getBoundingBoxCenter(polygons[0]));
      return true;
    } else { 
      setPolygonPaths([]);
      setMapCenterByCity(city);
      return false;
    }
  };

  const handleJsonChangeChange = (geoJson: string) => {
    setFormValue(prevFormValue => ({
      ...prevFormValue,
      jsonGeometry: geoJson
    }));
    updatePolygon(geoJson);
  };

  return (
    <>
      <PageContent>
        <Form fluid ref={formRef}
          onCheck={setFormError}
          formValue={formValue}
          model={CityGeometrySchema}
          disabled={loading}
        >
          <Grid w='100%' fluid>
            <Row>
              <Col span={{xs:24, xl:8}} >
                <Form.Group>
                  <Form.Label>Json Geometry</Form.Label>
                  <Form.Control 
                    name="jsonGeometry" 
                    value={formValue.jsonGeometry ?? ''} 
                    accepter={Textarea} 
                    style={{height: '550px'}}
                    onChange={handleJsonChangeChange}
                  />
                </Form.Group>
              </Col>
              <Col span={{xs:24, xl:16}} style={{textAlign: 'center'}}>
                <Form.Group>
                  <Form.Label>Geometry On Map</Form.Label>
                    {isLoaded ? <GoogleMap
                      mapContainerStyle={{ width: '100%', height: '550px', borderRadius: 10 }}
                      center={mapCenter}
                      zoom={12}
                    >
                      <Polygon
                        paths={polygonPaths}
                        options={{
                          fillColor: '#2196f3',
                          fillOpacity: 0.4,
                          strokeColor: '#2196f3',
                          strokeOpacity: 1,
                          strokeWeight: 2
                        }}
                      />
                    </GoogleMap> : '' }                
                </Form.Group>
              </Col>              
            </Row>
          </Grid>
        </Form>

        <Divider />
        {errorModel && <ErrorMessage model={errorModel} style={{ marginTop: 20, marginBottom:20 }}></ErrorMessage>}

        <Stack justifyContent="space-between">
          <IconButton icon={<PagePreviousIcon />} onClick={() => nav(-1)}>
            Back
          </IconButton>
          <IconButton
            icon={<PageNextIcon />}
            placement="right"
            appearance="primary"
            onClick={handleSubmit}
            loading={loading}
          >
            Save
          </IconButton>
        </Stack>
      </PageContent>
    </>
  );
};

export default CityGeometryForm;
